/* global window */
import React from 'react';
import {
  put, select, takeLatest, call, takeEvery, all,
} from 'redux-saga/effects';
import { delay } from 'redux-saga';
import R from 'ramda';
import { replace as routerReplace, push as routerPush } from 'react-router-redux';

import { jsonParseFn } from '../../../utils/string';
import {
  contracting as contractingApis,
  account as accountApis,
  downloads as downloadApis,
  workspace as wpApis,
  historyFeed as hfApi,
  fileUrls as fileApi,
  profile as profileApi,
} from '../../../api';
import composeDraftEditDataByType from './composeDraftEditDataByType';
import { apiSaga } from '../../../sagas';
import * as conListConst from '../../../stores/contracting/contract-list/contract-list-constants';
import * as cnt from './constants';
import * as wpCnts from '../../workspace/store/constants';
import { triggerError } from '../../../stores/global-errors-display/global-errors-display-actions';

import {
  updateProps,
} from '../../../stores/contracting/contract-list/contract-list-actions';

function* fetchContracts() {
  const contractPeriod = yield select(R.path(['contracting', 'requestsWithSameNameOrDomainDialog', 'contractPeriod']));
  const searchCompanyName = yield select(R.path(['contracting', 'requestsWithSameNameOrDomainDialog', 'searchCompanyName']));
  const searchDomain = yield select(R.path(['contracting', 'requestsWithSameNameOrDomainDialog', 'searchDomain']));
  yield* apiSaga(
    'GET_CONTRACT_REQUESTS_BY_COMPANY',
    contractingApis.getContractList,
    [{
      currentYear: contractPeriod,
      listType: 'search',
      searchCategory: 'sent',
      searchingAllYears: false,
      page: 1,
      searchInput: searchCompanyName || searchDomain,
    }],
  );
}

function* archiveSelectedContracts() {
  const selectedIds = yield select(R.path(['contracting', 'requestsWithSameNameOrDomainDialog', 'selectedIds']));
  yield* apiSaga(
    'DISCARD_SELECTED_IN_REQUESTS_WITH_SAME_NAME_DIALOG',
    contractingApis.deleteOrUndelete,
    [{ contractIds: selectedIds, state: true }],
  );
}

function* populateEditDialog() {
  const dialogForEdit = yield select(state => state.contracting.addEditContractDialog.toJS());
  const {
    ratePackageIndex: ratePackageIndexForEdit,
    productId: productIdForEdit,
    ratePackageName: ratePackageNameForEdit,
    ratePackageId: ratePackageIdForEdit,
    policiesNotesPage,
    type,
  } = dialogForEdit;
  if (type) {
    const contractActivitiesProducts = yield select(
      state => state.contracting.contractActivitiesProducts.toJS(),
    );
    const {
      rightSideContent: draftContentInView,
      leftNav: draftMetaData,
    } = contractActivitiesProducts;
    const { contractId: draftId } = draftMetaData;
    const { ratePackageIndex: ratePackageIndexInView, productId } = draftContentInView;
    // the edit happens while user is in policies and notes page
    const needToGoToNewProduct = !isNaN(ratePackageIndexForEdit) && ratePackageIndexForEdit !== ratePackageIndexInView;
    // the edit happens while user is in product page
    const needToGoToNotesPolicies = policiesNotesPage && productId;
    if (needToGoToNewProduct || needToGoToNotesPolicies) {
      if (needToGoToNewProduct) {
        const rpId = (() => {
          if (ratePackageIdForEdit) return ratePackageIdForEdit;
          return ratePackageNameForEdit ? encodeURIComponent(ratePackageNameForEdit) : 'Primary';
        })();
        yield put(routerReplace(`/contracting/draft/${draftId}/product/${productIdForEdit}/${rpId}`));
      }
      if (needToGoToNotesPolicies) {
        yield put(routerReplace(`/contracting/draft/${draftId}/policies`));
      }
    } else {
      const composedData = composeDraftEditDataByType(dialogForEdit, contractActivitiesProducts);
      if (type === 'copyDataFromAnotherProduct') {
        if (composedData.noShowIfEmpty && R.isEmpty(composedData.addedProducts)) yield put({ type: 'CLOSE_ADD_EDIT_CONTRACT_DIALOG' });
        else {
          yield call(delay, 300);
          yield put({
            type: 'OPEN_AND_POPULATE_ADD_EDIT_CONTRACT_DIALOG',
            payload: composedData.offersOrExtras ? composedData : { data: composedData },
          });
        }
      } else {
        yield put({
          type: 'OPEN_AND_POPULATE_ADD_EDIT_CONTRACT_DIALOG',
          payload: composedData && composedData.offersOrExtras ? composedData : { data: composedData },
        });
      }
    }
  }
}

function* createProductOrImport(payload) {
  const {
    user: { type },
    isDummyAccount,
  } = yield select(state => state.user.toJS());
  const isTrueSupplier = type === 'Supplier' && !isDummyAccount;
  // show import dialog when user hasn't edited the draft
  if (payload.hasSeenResponseWizard === false && (!isTrueSupplier || (payload.templates && payload.templates.length))) {
    yield put({
      type: 'INITIATE_ADD_EDIT_CONTRACT_DIALOG_WITH_TYPE',
      payload: {
        type: 'importFromTemplate',
        data: { forNewDraft: true },
      },
    });
  } else if (R.isEmpty(payload.productsArray)) {
    yield put({
      type: 'INITIATE_ADD_EDIT_CONTRACT_DIALOG_WITH_TYPE',
      payload: {
        type: 'addProducts',
        data: { isFirstProduct: true },
      },
    });
  }
}

function* getRespondedContractsForRequest(type, requestId) {
  yield* apiSaga(
    type,
    contractingApis.getRespondedContractList,
    [{ requestId }],
  );
}

// for notes page in contract
function* getContractNotes({ payload } = {}) {
  const historyFeedId = payload && payload.historyFeedId
    ? payload.historyFeedId
    : yield select(state => state.contracting.contractActivitiesProducts.getIn(['leftNav', 'historyFeedId']));
  yield* apiSaga(cnt.GET_NOTES, wpApis.getActivities, [{ assetId: historyFeedId, activityType: 'note' }]);
}

function* deleteContractNote({ payload, type }) {
  yield* apiSaga(type, wpApis.deleteMessage, [payload]);
}

function* afterGettingMetaData() {
  const dialogForEdit = yield select(state => state.contracting.addEditContractDialog.toJS());
  const { leftNav } = yield select(state => state.contracting.contractActivitiesProducts.toJS());
  if (dialogForEdit.type) return;
  const isInitialDraft = leftNav.contractState === 'draft' && leftNav.originFrom === 'initial';
  const isContractRequest = !leftNav.locationId;
  const isInNotes = window.location.pathname.indexOf('notes') > -1;
  if (isInitialDraft) {
    yield createProductOrImport(leftNav);
  }
  const docId = leftNav.contractId;
  if (isContractRequest) {
    yield getRespondedContractsForRequest('[contract-request]GET_RESPONDED_CONTRACT_LIST', docId);
  }
  if (isInNotes) {
    yield getContractNotes();
  }
}

function* triggerLoadContractList(action = {}) {
  if (window.location.pathname.indexOf('contract-list') > -1) {
    const { payload, type } = action;
    const notTogglingAdvanceSearch = !Boolean(type === '[contract-list]UPDATE_SEARCH_PARAMS' && R.keys(payload).length === 1 && R.has('openAdvanceSearch', payload));
    if (notTogglingAdvanceSearch) {
      const {
        pathname: currentRoute,
      } = yield select(R.path(['routing', 'locationBeforeTransitions']));
      if (currentRoute.indexOf('contract-list') > -1) {
        yield put({ type: 'LOAD_CONTRACT_LIST_FROM_BACKEND', payload: true });
      }
    }
  } else {
    const { leftNav } = yield select(state => state.contracting.contractActivitiesProducts.toJS());
    yield getRespondedContractsForRequest('[contract-request]GET_RESPONDED_CONTRACT_LIST', leftNav.contractId);
  }
}

function* getContractList() {
  const currentYear = yield select(R.path(['contracting', 'currentYear']));
  const { list, searchParams } = yield select(state => state.contracting.contractList.toJS());
  const params = {
    listType: (list.listType || searchParams.searchCategory || 'recent'),
    page: list.page || 1,
    currentYear,
    ...searchParams,
  };
  yield* apiSaga(
    conListConst.LOAD_CONTRACT_LIST,
    contractingApis.getContractList,
    [params]
  );
}

function* doUpdateFolders() {
  const { folders, originalFolder, newFolderName } = yield select(state => state.contracting.contractList.toJS());
  yield* apiSaga(
    '[contract-list]UPDATE_FOLDER_NAME',
    contractingApis.updateFolders,
    [{ folders, originalFolder, newFolderName }]
  );
  yield put({ type: '[contract-list]CHANGE_ROUTE', payload: { routeName: newFolderName, isFolder: true } });
}

function* doChangeContractListRoute() {
  const { searchParams, list } = yield select(state => state.contracting.contractList.toJS());
  const uriEncoded = encodeURIComponent(list.listType || '');
  yield put(routerReplace({
    pathname: `/contracting/contract-list/${uriEncoded}`,
    query: { isFolder: searchParams.isFolder },
  }));
  yield triggerLoadContractList();
}

function* getRequestForSupplierWizard() {
  const requestId = yield select(state => state.contracting.supplierWizard.toJS().contractRequest.contractId);
  if (requestId) {
    yield* apiSaga(
      '[supplier-wizard]GET_CONTRACT_REQUEST_INFO_SUPPLIER_WIZARD',
      contractingApis.getContractRequestInfo,
      [{ requestId }]
    );
  }
}

function* downloadContractingDocs() {
  const payload = yield select(R.path(['contracting', 'downloadDocs']));
  if (!payload) return;
  const payload2 = R.pick([
    'collection',
    'locale',
    'ratesOnly',
    'downloadRateFormat',
    'forAL',
  ], payload);
  const docIds = (() => {
    if (!R.isEmpty(payload.contractIds || [])) {
      return payload.contractIds;
    }
    if (!R.isEmpty(payload.templates || [])) {
      return payload.templates.map(({ _id }) => _id);
    }
    if (payload.docId) {
      return [payload.docId];
    }
    return []; // ?? nothing to do
  })();
  if (docIds.length === 1 && R.last(payload2.downloadRateFormat.split('_')) === 'singlefile') {
    // should not be single file if it is a single doc
    payload2.downloadRateFormat = payload2.downloadRateFormat.split('_').splice(0, 2).join('_');
  }
    // return R.isEmpty(payload.contractIds) ? [payload.docId] : payload.contractIds;
  // });
  yield apiSaga(
    '[contracting-download] downloadGroup',
    fileApi.downloadGroup,
    [{
      ...payload2,
      docIds,
    }],
  );
}

function* downloadContractingDocsSuccess({ type, payload }) {
  const { forAL } = yield select(R.path(['contracting', 'downloadDocs']));
  if (payload.status === 'processed' && payload.returnValue) {
    const url = R.path(['url'], jsonParseFn(payload.returnValue));
    // check if w ehave a return value URL or product an error
    if (url) {
      // success
      return yield put({
        type: `${type}_SUCCESS`,
        payload: { url },
      });
    }
  }
  if (payload.status === 'pending' && payload.jobUrl) { // still pending
    yield call(delay, 3e3);
    return yield apiSaga(
      '[contracting-download] downloadGroup',
      fileApi.checkJobUrl,
      [{
        ...payload,
        forAL,
      }],
    );
  }
  // error
  return yield put({
    type: `${type}_ERROR`,
  });
}

function downloadContractingUrl({ payload }) {
  const a = document.createElement('a');
  a.download = true;
  a.target = '_blank';
  a.setAttribute('download', true);
  a.href = payload.url;
  a.dispatchEvent(new MouseEvent('click'));
  // window.open(payload.url, '_blank');
}

function* shouldAskToAccept({ type }) {
  /*
  if in assets landing page
    check if user click a sinle contract or multiple contracts
    check if those contracts are in pending status, if so, prompt "would you like to accept?"
  if in single contract page
    check if that contract is in pending status
  else close dialog for single doc download, or how success for multi doc download
   */
  const { contractIds, docId } = yield select(state => state.contracting.downloadDocs);
  if (window.location.pathname.indexOf('assets-landing') > -1) {
    const { rates, viewAs } = yield select(state => state.assetsLandingReducer);
    const singleContractIsPending = docId && rates.find(o => o._id === docId && o.state === 'pending');
    const someBulkDownloadedContractsArePending = contractIds
      && contractIds.length
      && contractIds.some(id => rates.find(o => o._id === id && o.state === 'pending'));
    if (
      viewAs === 'Contractor'
      && (singleContractIsPending || someBulkDownloadedContractsArePending)
    ) {
      yield put({ type: '[contracting-download]ASK_ACCEPT_AFTER_DOWNLOAD' });
    } else if (type === '[contracting-download]DOWNLOAD_SINGLE_CONTRACT_DOC_SUCCESS') {
      yield put({ type: '[contracting-download]CLOSE_DOWNLOAD_POPOVER' });
    }
  } else if (window.location.pathname.indexOf('/contracting/contract/') > -1) {
    const currentState = yield select(state => state.contracting.contractActivitiesProducts.getIn(['leftNav', 'state']));
    const viewAs = yield select(state => state.user.getIn(['company', 'companyType']));
    if (currentState === 'pending' && viewAs === 'Contractor') {
      yield put({ type: '[contracting-download]ASK_ACCEPT_AFTER_DOWNLOAD' });
    } else if (type === '[contracting-download]DOWNLOAD_SINGLE_CONTRACT_DOC_SUCCESS') {
      yield put({ type: '[contracting-download]CLOSE_DOWNLOAD_POPOVER' });
    }
  } else if (type === '[contracting-download]DOWNLOAD_SINGLE_CONTRACT_DOC_SUCCESS') {
    yield put({ type: '[contracting-download]CLOSE_DOWNLOAD_POPOVER' });
  }
}

function* acceptRatesAfterDownload({ type }) {
  const { contractIds, docId, forAL } = yield select(state => state.contracting.downloadDocs);
  yield apiSaga(type, contractingApis.updateContractStatus, [{
    contractIds: contractIds && contractIds.length ? contractIds : [docId],
    state: 'accepted',
    forAL,
  }]);
}

function* exportExcel({ type, payload: { exportType } }) {
  const { docId, collection } = yield select(R.path(['contracting', 'downloadDocs']));
  if (docId) {
    yield apiSaga(type, downloadApis.getXls, [collection, docId, exportType]);
  }
}

function* handleExportExcel({ payload: { location } }) {
  yield put({ type: '[contracting-download]CLOSE_DOWNLOAD_POPOVER' });
  yield put(triggerError({ errorMessageObj: (<a href={location} target="_blank" rel="noopener noreferrer">Click to Download</a>) }));
  window.open(location);
}

function* handleExportExcelError() {
  yield put({ type: 'SHOW_CONTRACTING_ERROR_STRING', payload: 'Unable to generate quickview file' });
  yield* put({ type: '[contracting-download]CLOSE_DOWNLOAD_POPOVER' });
}

function* handleArchiveUnarchiveTemplate({ payload }) {
  yield* apiSaga(
    'ARCHIVE_UNARCHIVE_TEMPLATE',
    contractingApis.archiveUnarchiveTemplate,
    [payload],
  );
}

function* updateContractRequestPartner() {
  const {
    updatePartnerForAllContracts,
    contractId,
    newEmails,
    updatedEmails,
    removedEmails,
    newPartnerName,
    removeContactFromPartner,
  } = yield select(state => state.contracting.resendContractDialog.toJS());
  yield* apiSaga(
    'UPDATE_CONTRACT_PARTNER_INFO',
    contractingApis.updatePartnerForSentDoc,
    [{
      contractId,
      newEmails: newEmails.filter(s => s && s.trim()).map(s => s.toLowerCase()),
      updatedEmails,
      newPartnerName: (newPartnerName || '').trim(),
      removedEmails,
      updatePartnerForAllContracts,
      removeContactFromPartner,
    }]
  );
}

function* submitContractingComment({ type, payload }) {
  const { files, message, contractId, historyFeedId } = payload;
  yield* apiSaga(
    type,
    contractingApis.submitContractingComment,
    [{
      files,
      message,
      contractId,
      historyFeedId,
    }]
  );
}

function* getContractForwardingEmail({ type, payload }) {
  yield* apiSaga(
    type,
    contractingApis.getContractForwardingEmail,
    [payload],
  );
}

function* getDiffReport({ type, payload }) {
  yield* apiSaga(
    type,
    contractingApis.getDiffReport,
    [payload]
  )
}
function* getDiffReportForDraft() {
  const contractActivitiesProducts = yield select(state => state.contracting.contractActivitiesProducts.toJS());
  const { leftNav: draftMetaData } = contractActivitiesProducts;
  const { contractId: draftId } = draftMetaData;
  yield* apiSaga(
    'GET_DIFF_SUMMARY_FOR_DRAFT',
    contractingApis.getDiffReport,
    [{ docs: `draft:${draftId}:${draftId}`, onlyGetSummary: true }]
  )
}
function* getAvailDocsForDiffReport({ type, payload }) {
  if (payload.collection === 'template') {
    yield* apiSaga(
      type,
      contractingApis.getTemplates,
      [{ query: { locationId: payload.locationId } }]
    )
  } else {
    yield* apiSaga(
      type,
      contractingApis.getContracts,
      [{
        query: Object.assign({},
          { locationId: payload.locationId },
          payload.buyerId ? { ownerIds: payload.buyerId } : {},
          payload.email ? { toEmails: payload.email } : {}
        ),
      }]
    )
  }
}
function* getDiffReportForViewChanges({ type, payload }) {
  if (payload.open) {
    yield* getDiffReportForDraft();
  }
}

function* doSendReviewRatesEmail({ type }) {
  const {
    leftNav,
  } = yield select(state => state.contracting.contractActivitiesProducts.toJS());
  const {
    company: {
      companyId,
      companyName,
    },
  } = yield select(state => state.user.toJS());
  yield* apiSaga(type, accountApis.sendInternalEmail, [{
    type: 'reviewByExpert',
    company: {
      companyId,
      companyName,
    },
    draft: {
      draftId: leftNav.contractId,
      name: leftNav.templateName,
    },
  }]);
}

function* updateTemplateListUrl({ payload }) {
  // only pre set showType (how we group templates) if not defined by url already
  if (
    window.location.pathname.indexOf('template-list') > -1
    && window.location.pathname.indexOf('showType') === -1
  ) {
    yield put(routerReplace({
      pathname: '/contracting/template-list',
      query: {
        showType: payload.locations.length > 1 ? 'location' : '',
      },
    }));
  }
}

function* manageExtrasOffers({ type, payload = {} }) {
  const {
    addEditContractDialog,
    contractActivitiesProducts,
  } = yield select(state => state.contracting);
  const { offersOrExtras } = addEditContractDialog.toJS();
  yield* apiSaga(
    type,
    contractingApis.manageExtrasOffers,
    [{
      type: offersOrExtras.type,
      list: payload.list || offersOrExtras.list,
      draftId: contractActivitiesProducts.getIn(['leftNav', 'contractId']),
    }],
  );
}

export function* importSuccess() {
  const pathname = window.location.pathname;
  const splited = pathname.split('/');
  const collection = splited[2];
  const docId = splited[3];
  window.location = `${window.location.protocol}//${window.location.host}/contracting/${collection}/${docId}/details`;
}

export function* refreshContractDoc(options = {}) {
  const { bgRefresh } = options;
  const pathname = window.location.pathname;
  const splited = pathname.split('/');
  const collection = splited[2] === 'workspace' ? 'contract' : splited[2];
  const docId = splited[3];
  const isProduct = splited[4] === 'product';
  if (!bgRefresh) {
    yield put({ type: cnt.REFRESH_STARTED });
  }
  const refreshLeftNav = apiSaga(
    cnt.REFRESH_LEFT_NAV,
    contractingApis.getContractInfo,
    [{
      collection,
      docId,
      type: 'leftNav',
    }],
  );
  const refreshCompletionCheck = apiSaga(
    cnt.REFRESH_COMPLETION_CHECK,
    contractingApis.checkContractReadiness,
    [docId],
  );
  const refreshProduct = apiSaga(
    cnt.REFRESH_PRODUCT_DETAILS,
    contractingApis.getContractProductInfo,
    [{
      collection,
      docId,
      productId: splited[5],
      ratePackage: decodeURIComponent(splited[6] || ''),
    }],
  )
  const refreshPolicyNotes = apiSaga(
    cnt.REFRESH_POLICIES_NOTES,
    contractingApis.getContractInfo,
    [{
      collection,
      docId,
      type: 'policyNotes',
    }],
  );
  // const items = [];
  if (isProduct) {
    // items.push(refreshProduct);
    yield refreshProduct;
  } else {
    // items.push(refreshPolicyNotes);
    yield refreshPolicyNotes;
  }
  yield put({ type: cnt.REFRESH_FINISHED });
  // items.push(refreshLeftNav);
  yield refreshLeftNav;
  if (collection === 'draft') {
    // items.push(refreshCompletionCheck);
    yield refreshCompletionCheck;
  }
  // yield all(items);
}


function* afterUpdateContractPartnerInfoSuccess() {
  if (window.location.pathname.indexOf('/contracting/contract/') > -1) {
    yield refreshContractDoc();
  } else {
    yield triggerLoadContractList();
  }
}

function* updateSameAsCorporate({ type, payload }) {
  const {
    rightSideContent: {
      sameAsCorporatePolicyNotes,
    },
  } = yield select(state => state.contracting.contractActivitiesProducts.toJS());
  const updated = payload.checked
    ? [...sameAsCorporatePolicyNotes, payload.sectionValue]
    : sameAsCorporatePolicyNotes.filter(str => str !== payload.sectionValue);
  yield* apiSaga(
    type,
    contractingApis.updateSameAsCorporatePolicyNotes,
    [{
      templateDraftId: payload.templateDraftId,
      sameAsCorporatePolicyNotes: updated,
    }],
  );
}

function* removeMasterTemplate({ type }) {
  const {
    leftNav: {
      contractId: templateDraftId,
    },
  } = yield select(state => state.contracting.contractActivitiesProducts.toJS());
  yield* apiSaga(
    type,
    contractingApis.removePolicyNotesTemplate,
    [templateDraftId],
  );
}

function* refreshTemplateList() {
  yield* apiSaga(
    cnt.REFRESH_TEMPLATE_LIST,
    contractingApis.getTemplateList,
    [],
  );
}

function* afterRemoveDraftFromQueue({ args }) {
  if (args[0].refreshSection === 'doc') yield refreshContractDoc();
  if (args[0].refreshSection === 'list') yield refreshTemplateList();
}
function* sendTourWriter({ payload: { contractId, blackout } = {} }) {
  const selectedContracts = contractId ? [contractId] : (yield select(state => state.contracting.contractList.get('selectedContracts').toJS()));
  const { docId, collection } = yield select(R.path(['contracting', 'downloadDocs']));
  const tourwriterType = blackout ? 'tourwriterblackout' : 'tourwriter';
  if (selectedContracts.length > 0) {
    yield apiSaga(cnt.SEND_TOURWRITER, downloadApis.getXls, ['contract', selectedContracts.join('|'), tourwriterType]);
    yield put(updateProps({ selectedContracts: [], allContractIds: [] }));
  } else if (docId) {
    yield apiSaga(cnt.SEND_TOURWRITER, downloadApis.getXls, [collection, docId, tourwriterType]);
  }
}
function* tourWriterStart() {
  yield put(triggerError({ errorMessage: 'Generating File' }));
}
function* tourWriterError() {
  yield put(triggerError({ errorMessage: 'Cound not generate the file' }));
}

function* removeSingleFeedByTCAdmin({ type, payload }) {
  const confirmed = window.confirm('Are you sure?');
  if (confirmed) {
    yield apiSaga(type, contractingApis.removeSingleFeedByTCAdmin, [payload]);
  }
}
function *markHistoryFeedAsRead({ payload: { response: { historyFeedId, messageArray } } }) {
  const currentRoute = (yield select(R.path(['routing', 'locationBeforeTransitions', 'pathname']))).split('/');
  if (currentRoute[1] === 'contracting'
    && currentRoute[2] === 'contract'
    && currentRoute[currentRoute.length - 1] === 'activities'
    && messageArray.length > 0) {
    yield apiSaga(
      '[contracting] CONVERSATIONS_READ_WITH_PARAMS',
      wpApis.conversationsRead,
      [{
        assetId: historyFeedId,
        assetType: 'contract',
      }],
    );
  }
}
function *updateContractActivities(contractId) {
  yield apiSaga('LOADING_HISTORY_FEEDS', hfApi.getHistory, [contractId]);
}

function *getTemplateChangeLogs(threadId, search) {
  yield apiSaga('[contract-template]GET_CHANGE_LOGS', contractingApis.getTemplateChangeLog, [threadId, search]);
}

function *onRouteChange({ payload: { pathname, search } }) {
  const contractIdForActivitiesPage = (pathname.match(/^\/contracting\/contract\/([a-f\d]{24})\/activities/) || [])[1];
  if (contractIdForActivitiesPage) {
    yield updateContractActivities(contractIdForActivitiesPage);
  }

  const threadIdForChangeLogs = (pathname.match(/^\/contracting\/template\/([a-f\d]{24})\/logs/) || [])[1];
  if (threadIdForChangeLogs) {
    yield getTemplateChangeLogs(threadIdForChangeLogs, search);
  }
}

function* fileContractForRequest({ type, payload }) {
  yield apiSaga(type, contractingApis.fileContractForRequest, [payload]);
}
function* fileContractForRequestSuccess() {
  const pathname = window.location.pathname;
  const splited = pathname.split('/');
  const docId = splited[3];
  yield getRespondedContractsForRequest('[contract-request]GET_RESPONDED_CONTRACT_LIST', docId);
}
function* removeContractsFromRequests({ type, payload }) {
  yield apiSaga(type, contractingApis.removeContractsFromRequests, [payload]);
}
function* removeContractsFromRequestsSuccess({ payload: { requestId } }) {
  yield getRespondedContractsForRequest('[contract-request]GET_RESPONDED_CONTRACT_LIST', requestId);
}
function* handleOpenReSendContractDialog({ payload: { contractId } }) {
  yield apiSaga('GET_PARTNER_FOR_RESEND_DIALOG', contractingApis.getContractPartners, [{ contractId }]);
}
function* handleAfterMadeACopyOfATemplate() {
  if (window.location.href.indexOf('template-list') > -1) {
    yield refreshTemplateList();
  }
}

function* makeACopyForBuyer({ type, payload }) {
  yield apiSaga(type, contractingApis.makeACopyForBuyer, [payload]);
}

function* updateWorkspaceContract({ type, payload }) {
  yield apiSaga(type, contractingApis.updateWorkspaceContract, [payload]);
}

function* requestDL() {
  const { contractId, rateConversionSetting, messageForDL, priority } = yield select(state => state.contracting.requestDL);
  yield apiSaga('REQUEST_DL', contractingApis.requestDL, [{ contractId, rateConversionSetting, messageForDL, priority }]);
}

function* afterRequestDLSuccess() {
  if (window.location.href.indexOf('contract-list') > -1) {
    yield triggerLoadContractList();
  } else {
    yield refreshContractDoc();
  }
}

function* searchCCTemplates({ type, payload }) {
  yield apiSaga(type, contractingApis.getTemplatesForLocOrCC, [payload])
}

function* getProductsForOmniImport({ type, payload }) {
  yield apiSaga(type, contractingApis.getContractInfo, [{
    collection: payload.template.collection,
    docId: payload.template.idFromTCAdmin,
    type: 'leftNav',
  }]);
}

function* doOmniImport({ type, payload }) {
  yield apiSaga(type, contractingApis.omniImport, [payload]);
}

function* editProductProfile({ type, payload }) {
  yield apiSaga(type, contractingApis.updateProductNameBeddingConfig, [payload]);
}

function* editProductProfileSuccess({ payload, args }) {
  // potentially swapping the productId when user tries to update product name and there is already one exist
  if (args[0].productId !== payload.productId) {
    yield put(routerReplace(`/contracting/draft/${args[0].contractId}/product/${payload.productId}/${payload.ratePackageId}`));
  }
  yield refreshContractDoc({ bgRefresh: true });
}

// UPDATE_NEW_EMAIL_IN_CONTRACT
export default [
  takeLatest('[supplier-wizard]GET_CONTRACT_REQUEST_INFO_SUPPLIER_WIZARD', getRequestForSupplierWizard),
  takeLatest('OPEN_DIALOG_SHOW_REQUESTS_WITH_SAME_NAME', fetchContracts),
  takeLatest('DISCARD_SELECTED_IN_REQUESTS_WITH_SAME_NAME_DIALOG', archiveSelectedContracts),
  takeLatest('INITIATE_ADD_EDIT_CONTRACT_DIALOG_WITH_TYPE', populateEditDialog),
  takeLatest('PRODUCTS_SAVED_UPDATE_REDUX', populateEditDialog),
  takeLatest('GET_PRODUCT_DETAILS', populateEditDialog),
  takeLatest('GET_CONTRACT_DETAILS', populateEditDialog),
  takeLatest('GET_CONTRACT_LEFT_NAV', afterGettingMetaData),
  takeLatest('[contract-list]UPDATE_SEARCH_PARAMS', triggerLoadContractList), // trigger
  takeLatest('[contracting]SELECTED_YEAR_FOR_CONTRACTING', triggerLoadContractList), // trigger
  takeLatest('[contract-list]RESET_SEARCH_PARAMS', triggerLoadContractList), // trigger
  takeLatest('MASS_ASSIGN_FOLDER_CONTRACT_LIST', triggerLoadContractList), // trigger
  takeLatest('MASS_DELETE_RESTORE_CONTRACT_LIST', triggerLoadContractList), // trigger
  takeLatest('[contract-list] MASS_ARCHIVE_OR_RESTORE', triggerLoadContractList), // trigger
  takeLatest('[contract-list]UPDATE_FOLDER_NAME', doUpdateFolders),
  takeLatest('[contract-list]CHANGE_ROUTE', doChangeContractListRoute),
  takeLatest('LOAD_CONTRACT_LIST_FROM_BACKEND', getContractList),
  takeLatest('[contracting-download]DOWLOAD_CONTRACTING_DOCS', downloadContractingDocs),
  takeEvery('[contracting-download] downloadGroup_SUCCESS', downloadContractingDocsSuccess),
  takeEvery('[contracting-download] downloadGroup_SUCCESS_SUCCESS', downloadContractingUrl),
  takeLatest('[contracting-download]ACCEPT_RATES', acceptRatesAfterDownload),
  takeLatest('[contracting-download]DOWNLOAD_SINGLE_CONTRACT_DOC_SUCCESS', shouldAskToAccept),
  takeLatest('[contracting-download]BULK_DOWNLOAD_SUCCESS', shouldAskToAccept),
  takeLatest('[contracting-download]downloadGroup_SUCCESS_SUCCESS', shouldAskToAccept),
  takeLatest('[contracting-download]EXPORT_EXCEL', exportExcel),
  takeEvery('[contracting-download]EXPORT_EXCEL_SUCCESS', handleExportExcel),
  takeEvery('[contracting-download]EXPORT_EXCEL_ERROR', handleExportExcelError),
  takeLatest('ARCHIVE_UNARCHIVE_TEMPLATE', handleArchiveUnarchiveTemplate),
  takeLatest('UPDATE_CONTRACT_PARTNER_INFO', updateContractRequestPartner),
  takeLatest('UPDATE_CONTRACT_PARTNER_INFO_SUCCESS', afterUpdateContractPartnerInfoSuccess), // trigger
  takeLatest('SUBMIT_CONTRACTING_COMMENT', submitContractingComment),
  takeEvery(conListConst.GET_CONTRACT_EMAIL, getContractForwardingEmail),
  takeLatest('GET_CONTRACTING_DIFF_REPORT', getDiffReport),
  takeLatest('GET_AVAIL_DOCS_FOR_DIFF_REPORTS', getAvailDocsForDiffReport),
  takeLatest('SEND_UPDATES_IN_PUBLISH_DRAFT_DIALOG', getDiffReportForDraft),
  takeLatest('TOGGLE_SHOW_DRAFT_CHANGES_POPOVER', getDiffReportForViewChanges),
  takeLatest(cnt.REVIEW_MY_RATES_DRAFT, doSendReviewRatesEmail),
  takeLatest('GET_SUPPLIER_LOCATIONS_LIST_IN_TEMPLATE_LIST', updateTemplateListUrl),
  takeLatest(cnt.MANAGE_EXTRAS_OFFERS, manageExtrasOffers),
  takeLatest(`${cnt.MANAGE_EXTRAS_OFFERS}_SUCCESS`, () => refreshContractDoc({ bgRefresh: true })),
  takeLatest('CONTRACT_PRODUCT_UPDATED', () => refreshContractDoc({ bgRefresh: true })),
  takeLatest(cnt.UPDATE_SAME_AS_CORPORATE_POLICY_NOTES, updateSameAsCorporate),
  takeLatest(`${cnt.UPDATE_SAME_AS_CORPORATE_POLICY_NOTES}_SUCCESS`, refreshContractDoc),
  takeLatest(cnt.REMOVE_MASTER_TEMPLATE, removeMasterTemplate),
  takeLatest(`${cnt.REMOVE_MASTER_TEMPLATE}_SUCCESS`, refreshContractDoc),
  takeLatest(`${cnt.REMOVE_DRAFT_FROM_QUEUE}_SUCCESS`, afterRemoveDraftFromQueue),
  takeLatest(`${cnt.ADD_DRAFT_TO_QUEUE}_SUCCESS`, refreshContractDoc),
  takeLatest('UPDATE_CONTRACT_PARTNER_INFO_SUCCESS', refreshContractDoc),
  takeLatest('RESEND_CONTRACT_FINISHED', refreshContractDoc),
  takeLatest(`${cnt.SEND_UPDATES_FROM_QUEUE}_SUCCESS`, refreshTemplateList),
  takeLatest(cnt.SEND_TOURWRITER, sendTourWriter),
  takeEvery(`${cnt.SEND_TOURWRITER}_SUCCESS`, handleExportExcel),
  takeEvery(`${cnt.SEND_TOURWRITER}_STARTED`, tourWriterStart),
  takeEvery(`${cnt.SEND_TOURWRITER}_ERROR`, tourWriterError),
  takeLatest(cnt.REMOVE_SINGLE_FEED_BY_TC_ADMIN, removeSingleFeedByTCAdmin),
  takeLatest(`${wpCnts.MESSAGE_CREATE}_SUCCESS`, getContractNotes),
  takeLatest('GET_HISTORY_FEEDS', markHistoryFeedAsRead),
  takeLatest('@@router/LOCATION_CHANGE', onRouteChange),
  takeLatest(cnt.FILE_CONTRACT_FOR_REQUEST, fileContractForRequest),
  takeLatest(`${cnt.FILE_CONTRACT_FOR_REQUEST}_SUCCESS`, fileContractForRequestSuccess),
  takeLatest(cnt.REMOVE_CONTRACT_FROM_REQUEST, removeContractsFromRequests),
  takeLatest(`${cnt.REMOVE_CONTRACT_FROM_REQUEST}_SUCCESS`, removeContractsFromRequestsSuccess),
  takeLatest('SEARCH_CC_RATES_TEMPLATES', searchCCTemplates),
  takeLatest('LOAD_PRODUCTS_FOR_OMNI_IMPORT_SOURCE_TEMPLATE', getProductsForOmniImport),
  takeLatest('OMNI_IMPORT', doOmniImport),
  takeLatest('OMNI_IMPORT_SUCCESS', importSuccess),
  takeLatest('IMPORT_DATA_FROM_ANOTHER_TEMPLATE_SUCCESS', importSuccess),
  takeLatest('CHANGE_RESEND_CONTRACT_DIALOG_STATE', handleOpenReSendContractDialog),
  takeLatest('SAVED_TEMPLATE_AFTER_DRAFT_PUBLISHED', handleAfterMadeACopyOfATemplate),
  takeLatest('{dummy_account} makeACopyForBuyer', makeACopyForBuyer),
  takeLatest('REQUEST_DL', requestDL),
  takeLatest('REQUEST_DL_SUCCESS', afterRequestDLSuccess),
  takeLatest('[contract] UPDATE_CONTRACT_ASSIGNEE', updateWorkspaceContract),
  takeLatest('[contract] UPDATE_CONTRACT_ASSIGNEE_SUCCESS', afterRequestDLSuccess),
  takeLatest('{draft}EDIT_PRODUCT_PROFILE_INFO', editProductProfile),
  takeLatest('{draft}EDIT_PRODUCT_PROFILE_INFO_SUCCESS', editProductProfileSuccess),
  takeLatest('{contract} DELETE_CONTRACT_NOTE', deleteContractNote),
  takeLatest([
    '{contract} DELETE_CONTRACT_NOTE_SUCCESS',
    cnt.GET_NOTES,
  ], getContractNotes),
];



// WEBPACK FOOTER //
// ./src/app/components/contracting/__storeFiles__/sagas.jsx